import type { SubmitAiSearchFeedbackResponse } from '@/shared/api/graphql/aiSearch';
import { SUBMIT_AI_SEARCH_FEEDBACK } from '@/shared/api/graphql/aiSearch';
import { useTranslation } from '@/shared/lib/common/i18n';
import { useMutation } from '@apollo/client';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

const MAX_COMMENT = 1000;

type Props = {
  searchId: string;
  question: string;
};

/** Thumbs up/down under an answer. Voting again replaces the vote. Never blocks the answer: errors only go to the console. */
const AiSearchFeedback = ({ searchId, question }: Props) => {
  const { t } = useTranslation();
  const [vote, setVote] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);

  const [submit, { loading }] = useMutation<SubmitAiSearchFeedbackResponse>(SUBMIT_AI_SEARCH_FEEDBACK, {
    onCompleted: () => setSent(true),
    onError: error => {
      console.error(error);
    }
  });

  const send = (thumbsUp: boolean, text?: string) => {
    const trimmed = text?.trim();
    submit({ variables: { searchId, thumbsUp, question, ...(trimmed ? { comment: trimmed } : {}) } });
  };

  const handleVote = (thumbsUp: boolean) => {
    if (loading || vote === thumbsUp) return;
    setVote(thumbsUp);
    setSent(false);
    // A thumbs-down keeps the comment already in the box, so the saved comment matches what is shown.
    send(thumbsUp, thumbsUp ? undefined : comment);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <IconButton size="small" aria-label={t.aiSearch.feedback.thumbsUp} onClick={() => handleVote(true)}>
          {vote === true ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" aria-label={t.aiSearch.feedback.thumbsDown} onClick={() => handleVote(false)}>
          {vote === false ? <ThumbDownIcon fontSize="small" color="primary" /> : <ThumbDownOutlinedIcon fontSize="small" />}
        </IconButton>
        {sent && (
          <Typography variant="caption" sx={{ color: 'success.main', pl: 1 }}>
            {t.aiSearch.feedback.thanks}
          </Typography>
        )}
      </Stack>
      {vote === false && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            placeholder={t.aiSearch.feedback.commentPlaceholder}
            value={comment}
            onChange={event => setComment(event.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_COMMENT } }}
          />
          <Button size="small" variant="outlined" disabled={loading || !comment.trim()} onClick={() => send(false, comment)}>
            {t.aiSearch.feedback.send}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default AiSearchFeedback;
